
/**
 * Quantity Switcher Component
 * Handles quantity selection and cart integration for the quantity switcher feature
 * Now supports up to 3 products
 */

class QuantitySwitcher extends HTMLElement {
  constructor() {
    super();
    this.selectedQuantity = 3; // Default to Buy 3
    this.selectedVariants = {
      1: null,
      2: null,
      3: null
    };
  }

  connectedCallback() {
    this.init();
  }

  init() {
    console.log('Initializing QuantitySwitcher component');
    this.bindEvents();
    this.initializeVariants();
    this.updatePricing();
    console.log('QuantitySwitcher initialized with quantity:', this.selectedQuantity);
  }

  bindEvents() {
    // Quantity option selection
    this.querySelectorAll('[data-option]').forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const quantity = parseInt(e.currentTarget.dataset.option);
        console.log('Quantity option clicked:', quantity);
        this.selectQuantity(quantity);
      });
    });

    // Color swatch selection for all three products
    for (let i = 1; i <= 3; i++) {
      this.querySelectorAll(`[data-color-swatches-${i}] .quantity-switcher__color-swatch`).forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log(`Color swatch ${i} clicked:`, swatch.dataset.colorName);
          this.selectColor(i, e.currentTarget);
        });
      });
    }
  }

  initializeVariants() {
    console.log('Initializing variants');
    // Set initial selected variants for all three products
    for (let i = 1; i <= 3; i++) {
      const swatch = this.querySelector(`[data-color-swatches-${i}] .quantity-switcher__color-swatch.selected`);
      
      if (swatch) {
        this.selectedVariants[i] = {
          id: swatch.dataset.variantId,
          handle: swatch.dataset.productHandle,
          colorName: swatch.dataset.colorName,
          image: swatch.dataset.productImage,
          title: swatch.dataset.productTitle
        };
        console.log(`Initialized variant ${i}:`, this.selectedVariants[i]);
      }
    }
  }

  selectQuantity(quantity) {
    console.log('selectQuantity called with:', quantity);
    this.selectedQuantity = quantity;
    
    // Update UI
    this.querySelectorAll('[data-option]').forEach(option => {
      option.classList.remove('selected');
    });
    
    const selectedOption = this.querySelector(`[data-option="${quantity}"]`);
    if (selectedOption) {
      selectedOption.classList.add('selected');
      console.log('Updated selected option:', quantity);
    }
    
    // Show/hide product selectors based on quantity
    for (let i = 2; i <= 3; i++) {
      const selector = this.querySelector(`[data-product-selector="${i}"]`);
      if (selector) {
        if (quantity >= i) {
          selector.style.display = 'block';
          console.log(`Showing product selector ${i}`);
        } else {
          selector.style.display = 'none';
          console.log(`Hiding product selector ${i}`);
        }
      }
    }
    
    this.updatePricing();
    
    // Dispatch event for buy-buttons to listen to
    this.dispatchEvent(new CustomEvent('quantity-switcher:changed', {
      detail: {
        quantity: this.selectedQuantity,
        variants: this.selectedVariants
      },
      bubbles: true
    }));
  }

  selectColor(productIndex, swatch) {
    console.log('selectColor called for product', productIndex, 'with color:', swatch.dataset.colorName);
    
    // Update selected state
    this.querySelectorAll(`[data-color-swatches-${productIndex}] .quantity-switcher__color-swatch`).forEach(s => {
      s.classList.remove('selected');
    });
    swatch.classList.add('selected');
    
    // Update variant data
    this.selectedVariants[productIndex] = {
      id: swatch.dataset.variantId,
      handle: swatch.dataset.productHandle,
      colorName: swatch.dataset.colorName,
      image: swatch.dataset.productImage,
      title: swatch.dataset.productTitle
    };
    
    // Update UI
    const imageElement = this.querySelector(`[data-product-image-${productIndex}]`);
    const colorNameElement = this.querySelector(`[data-color-name-${productIndex}]`);
    const titleElement = this.querySelector(`[data-product-title-${productIndex}]`);
    
    if (imageElement) {
      imageElement.src = swatch.dataset.productImage;
    }
    if (colorNameElement) {
      colorNameElement.textContent = swatch.dataset.colorName;
    }
    if (titleElement) {
      titleElement.textContent = swatch.dataset.productTitle;
    }
    
    console.log('Updated variant data for product', productIndex, ':', this.selectedVariants[productIndex]);
    
    // Dispatch event for buy-buttons to listen to
    this.dispatchEvent(new CustomEvent('quantity-switcher:changed', {
      detail: {
        quantity: this.selectedQuantity,
        variants: this.selectedVariants
      },
      bubbles: true
    }));
  }

  updatePricing() {
    // Pricing will be handled by the buy-buttons component
    // This method is kept for future use if needed
  }

  formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }
}

// Register the custom element
customElements.define('quantity-switcher', QuantitySwitcher);

// Export for potential use in other modules
window.QuantitySwitcher = QuantitySwitcher;

// Fallback initialization for existing elements
document.addEventListener('DOMContentLoaded', function() {
  console.log('DOM loaded, checking for quantity switchers');
  const quantitySwitchers = document.querySelectorAll('[data-quantity-switcher]');
  console.log('Found', quantitySwitchers.length, 'quantity switchers');
  
  quantitySwitchers.forEach(element => {
    if (!element.quantitySwitcherInitialized) {
      console.log('Initializing quantity switcher element');
      element.quantitySwitcherInitialized = true;
      
      // Create a new instance and attach it to the element
      const switcher = new QuantitySwitcher();
      Object.setPrototypeOf(element, QuantitySwitcher.prototype);
      Object.setPrototypeOf(switcher, element);
      
      // Copy the switcher's methods to the element
      Object.getOwnPropertyNames(QuantitySwitcher.prototype).forEach(prop => {
        if (prop !== 'constructor') {
          element[prop] = switcher[prop].bind(element);
        }
      });
      
      // Initialize the element
      element.init();
    }
  });
});

// Also initialize on window load as a backup
window.addEventListener('load', function() {
  console.log('Window loaded, checking for quantity switchers again');
  const quantitySwitchers = document.querySelectorAll('[data-quantity-switcher]');
  
  quantitySwitchers.forEach(element => {
    if (!element.quantitySwitcherInitialized) {
      console.log('Initializing quantity switcher element on window load');
      element.quantitySwitcherInitialized = true;
      
      // Create a new instance and attach it to the element
      const switcher = new QuantitySwitcher();
      Object.setPrototypeOf(element, QuantitySwitcher.prototype);
      Object.setPrototypeOf(switcher, element);
      
      // Copy the switcher's methods to the element
      Object.getOwnPropertyNames(QuantitySwitcher.prototype).forEach(prop => {
        if (prop !== 'constructor') {
          element[prop] = switcher[prop].bind(element);
        }
      });
      
      // Initialize the element
      element.init();
    }
  });
});